import { useTranslation } from 'react-i18next'
import { getBuildVersionDisplay } from './BuildVersionBadge'
import { useVersionCheck } from '../../hooks/useVersionCheck'

/**
 * Non-blocking, bottom-center canvas prompt that appears when a newer frontend
 * build has been deployed while this tab is open (see `useVersionCheck`).
 *
 * Renders nothing until a new version is detected. Positioned above the bottom
 * docks (`z-[55]`, below MaintenanceBanner's `z-[60]`) at bottom-center, which
 * is free of the existing canvas docks (bottom-left controls, bottom-right
 * minimap/badge/toasts). Scoped to the workspace so it never collides with the
 * landing/login bottom-center error overlay.
 */
export function NewVersionPrompt() {
  const { t } = useTranslation('workspace')
  const { newVersion, dismiss, refresh } = useVersionCheck()

  if (!newVersion) return null

  const display = getBuildVersionDisplay(newVersion)
  // Only surface a human-facing version label (a build/semver like "v2.10.3").
  // A raw commit SHA is meaningless to users, so we hide it and show just the
  // title. When a real semver pipeline exists this lights up automatically.
  const humanVersion = display.labelType === 'build' ? display.value : undefined

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="new-version-prompt"
      data-new-version={newVersion}
      className="fixed bottom-6 left-1/2 z-[55] flex -translate-x-1/2 items-center gap-3 rounded-lg border border-border bg-surface-0/95 px-4 py-2.5 shadow-lg backdrop-blur-sm"
    >
      <span className="text-sm font-medium text-text-primary" data-testid="new-version-message">
        {humanVersion
          ? t('versionUpdate.titleWithVersion', { version: humanVersion })
          : t('versionUpdate.title')}
      </span>
      <button
        type="button"
        onClick={refresh}
        data-testid="new-version-refresh"
        className="rounded-md border border-primary bg-primary px-3 py-1 text-sm font-medium text-primary-foreground transition-colors hover:border-primary/80 hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/30"
      >
        {t('versionUpdate.refresh')}
      </button>
      <button
        type="button"
        onClick={dismiss}
        aria-label={t('versionUpdate.dismiss')}
        data-testid="new-version-dismiss"
        className="flex-shrink-0 rounded-md border border-transparent p-1 text-base leading-none text-text-muted transition-colors hover:border-input-border-hover hover:bg-surface-3 focus:outline-none focus:ring-2 focus:ring-primary/20"
      >
        ×
      </button>
    </div>
  )
}

export default NewVersionPrompt
